// src/components/Sidebar/SidebarCourses.tsx
"use client";
//
// 2026-08-07・当初は`SidebarCoursesLink.tsx`という単純な/coursesへのリンクだったが、
// Kazuto実機確認で「折りたたみじゃないと変・開いたら各コースが並んで、それぞれのコース
// ページに飛ぶのが自然」と指摘。SidebarCardType.tsx/SidebarTopics.tsxと同じアコーディオン
// パターンに作り直した（ファイル名もLink→Coursesに変更）。
//
// 一覧データはsrc/data/coursesConfig.tsを`/courses`ページと共有。
//
// 2026-08-07・Kazuto実機確認でさらに調整：当初はロック中の項目それぞれを`/courses`への
// リンクにしていたが、「名前の違う入り口を押しても全部同じページに飛ぶのは体験として変」
// と指摘。かといって完全にリンクを外すと、アクセスコードを入力したくなったときにサイドバー
// から`/courses`へ辿る手段が無くなり迷子になる、という懸念も出た。
// → 対応：ロック中の項目自体はリンクをやめ名前＋鍵アイコンのみの表示にし（誤解を生まない）、
// 代わりに一覧の先頭に共通の「購入・コードの入力はこちら →」を1本だけ配置する
// （所有状況に関わらず常時表示・`/courses`へのリンク）。所有済み項目はそのまま各ページへの
// 直接リンクを維持（Kazuto確認済み・「別にいい」）。

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useUser } from "@clerk/nextjs";
import { ChevronDown, Lock, ArrowRight, KeyRound } from "lucide-react";
import { useRecordContext } from "@/contexts/RecordContext";
import { toMs, getEffectivePlan } from "@/lib/planMap";
import { COURSES, TOOLS } from "@/data/coursesConfig";
import { TEST_ADMIN_UIDS } from "@/constants/testAccounts";

type SidebarCoursesProps = {
  openSection: string | null;
  setOpenSection: (section: string | null) => void;
};

export default function SidebarCourses({ openSection, setOpenSection }: SidebarCoursesProps) {
  const recordContext = useRecordContext();
  const { user } = useUser();
  // 2026-11-02：購入済みコースは RecordContext が共有している /api/user-courses の結果を使う（取得前・失敗時は空）。
  const ownedCourses: Record<string, unknown> = recordContext?.userCourses ?? {};

  // /editionsのワークツール判定・/courses/page.tsxと同じロジック。
  const hasPlanOrTrialAccess = useMemo(() => {
    const profile = recordContext?.userProfile as any;
    if (!profile) return false;
    const userPlan: string = getEffectivePlan(profile) ?? profile?.user_plan ?? "free";
    const isPaid = ["discovery", "discovery_pro", "facilitator", "partner"].includes(userPlan);
    if (isPaid) return true;
    const trialUntilMs = toMs(profile?.entitlements?.full_access_until);
    return trialUntilMs > Date.now();
  }, [recordContext?.userProfile]);

  const isOpen = openSection === "courses";
  const handleToggle = () => setOpenSection(isOpen ? null : "courses");

  // 【8/7・Kazuto決定】adminOnly項目（③の新規年間アクセス5枠）は当面
  // TEST_ADMIN_UIDSのアカウントにのみ表示する（/courses/page.tsxと同じゲート）。
  // 【8/7・バグ修正】/api/user-profileにuidフィールドが無くrecordContext.userProfile.uidは
  // 常にundefinedだったため、CourseGate.tsxと同じくClerkのuser.idを直接使う形に変更
  // （誰にも表示されなかった不具合の原因）。
  const isTestAdmin = useMemo(() => {
    const uid = user?.id;
    return !!uid && TEST_ADMIN_UIDS.includes(uid);
  }, [user?.id]);

  // 【8/8・CEO指示】hidden:trueの項目（コース中身が未確定のもの）は
  // adminOnly/TEST_ADMIN_UIDSに関わらず、管理者を含め誰にも表示しない。
  const items = [
    ...COURSES.filter((item) => !item.hidden).filter((item) => !item.adminOnly || isTestAdmin),
    ...TOOLS,
  ];

  return (
    <nav className="border-t pt-4 mt-6">
      <h2
        className="text-sm font-bold mb-2 cursor-pointer flex justify-between items-center text-gray-700"
        onClick={handleToggle}
      >
        コース
        <ChevronDown size={14} className={isOpen ? "rotate-180 transition-transform" : "transition-transform"} />
      </h2>

      {isOpen && (
        <div className="flex flex-col gap-1">
          <Link
            href="/courses"
            className="flex items-center gap-1 px-2 py-1 mb-1 text-xs text-gray-400 hover:text-gray-600 transition"
          >
            <KeyRound size={11} />
            購入・コードの入力はこちら
            <ArrowRight size={10} />
          </Link>

          {items.map((item) => {
            if (item.comingSoon) {
              return (
                <span
                  key={item.courseId}
                  className="flex items-center gap-1.5 px-2 py-1 text-sm text-gray-400"
                >
                  <Lock size={12} />
                  {item.label}（準備中）
                </span>
              );
            }

            // adminOnly項目は一覧表示の時点で既にTEST_ADMIN_UIDSに絞られているため、
            // 所有チェック（購入・コード）を経由させず常に解放扱いにする（2026-08-10・バグ修正）。
            // 2026-09-09・excludeFromPlanUnlock:true の項目（course_standard）は、
            // purchaseLinksがあってもhasPlanOrTrialAccessによる自動解放をしない
            // （TASKS_ACTIVE.md 8/23〜9/9エントリ「1万円層コースの購入導線構築」8/26確定）。
            const unlocked = item.adminOnly
              ? true
              : item.purchaseLinks && !item.excludeFromPlanUnlock
              ? hasPlanOrTrialAccess || Boolean(ownedCourses[item.courseId])
              : Boolean(ownedCourses[item.courseId]);

            if (unlocked) {
              return (
                <Link
                  key={item.courseId}
                  href={item.href}
                  className="px-2 py-1 rounded text-sm text-gray-700 hover:bg-gray-100 transition"
                >
                  {item.label}
                </Link>
              );
            }

            // ロック中は個別リンクにせず、名前＋鍵アイコンのみ（誤解を生まないため）。
            // 「購入・コードの入力はこちら」は上の共通リンクに集約済み。
            // 2026-10-14・CEO室：使い方20選（コードで開く運用なし）の行にだけ、小さく「購入する」を添える。
            // 他のコースの表示は変えない。
            const purchaseUrl =
              item.courseId === "course_standard" ? item.purchaseLinks?.[0]?.url : undefined;
            const row = (
              <span
                key={item.courseId}
                className="flex items-center gap-1.5 px-2 py-1 text-sm text-gray-400"
              >
                <Lock size={12} />
                {item.label}
                {purchaseUrl && (
                  <a
                    href={purchaseUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    購入する
                  </a>
                )}
              </span>
            );
            // 2026-10-15・CEO室：使い方20選の鍵付きの行の下に、無料の3枚語りへの入口を足す
            // （表示だけ。アクセス権限の判定は変えない）。
            if (item.courseId !== "course_standard") return row;
            return (
              <div key={item.courseId} className="flex flex-col">
                {row}
                <Link
                  href="/courses/essentials/flip/1"
                  className="ml-6 flex items-center gap-1.5 px-2 py-0.5 text-sm text-gray-700 hover:bg-gray-100 rounded transition"
                >
                  3枚語り
                  <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 border border-emerald-200">
                    無料
                  </span>
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </nav>
  );
}
